<template>
  <section class="waajo-ticket-panel waajo-ticket-followup-panel">
    <div class="waajo-ticket-panel-head">
      <h4><i class="fa fa-clock-o"></i> Auto Follow-up</h4>
      <button type="button" class="waajo-ticket-link-btn" :disabled="loading" @click="refresh">
        <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
      </button>
    </div>

    <div v-if="loading" class="waajo-ticket-followup-loading">
      <span class="waajo-ticket-followup-sk"></span>
      <span class="waajo-ticket-followup-sk is-short"></span>
    </div>

    <div v-else-if="error" class="waajo-ticket-followup-error">
      <i class="fa fa-exclamation-circle"></i>
      <span>{{ error }}</span>
      <button type="button" @click="refresh">Coba lagi</button>
    </div>

    <template v-else>
      <div v-if="!latest" class="waajo-ticket-followup-empty">
        <i class="fa fa-clock-o"></i>
        <p v-if="ticket.status === 'resolved'">Tiket sudah Resolved. Anda dapat menjadwalkan pesan tindak lanjut otomatis.</p>
        <p v-else>Auto Follow-up dapat dijadwalkan setelah tiket berstatus Resolved.</p>
        <button
          v-if="ticket.status === 'resolved'"
          type="button"
          class="waajo-ticket-btn is-primary is-block"
          :disabled="!canSchedule || submitting"
          @click="openPicker"
        >
          <i class="fa fa-calendar-plus-o"></i> Jadwalkan Auto Follow-up
        </button>
        <small v-if="blockedReason" class="waajo-ticket-followup-note">{{ blockedReason }}</small>
      </div>

      <template v-else>
        <div class="waajo-ticket-followup-card" :class="'is-' + latest.status">
          <div class="waajo-ticket-followup-card-top">
            <div>
              <strong>{{ latest.template_name || 'Auto Follow-up' }}</strong>
              <small v-if="latest.batch_name" class="waajo-ticket-followup-batch">Batch: {{ latest.batch_name }}</small>
            </div>
            <span class="waajo-ticket-followup-badge" :class="'is-' + latest.status">{{ statusLabel(latest.status) }}</span>
          </div>
          <div class="waajo-ticket-followup-meta">
            <div><span>Nomor WhatsApp CS</span><strong>{{ formatTicketPhone(latest.license_phone_number, latest.license_name || '-') }}</strong></div>
            <div><span>Pelanggan</span><strong>{{ ticket.contact_name || ticket.contact_number }}</strong></div>
            <div><span>Status penerima</span><strong>{{ recipientLabel(latest.recipient_status) }}</strong></div>
            <div><span>Jadwal berikutnya</span><strong>{{ formatTicketDateTime(latest.next_scheduled_at) }}</strong></div>
          </div>
          <small v-if="latest.stop_reason" class="waajo-ticket-followup-note">
            {{ stopReasonLabel(latest.stop_reason) }}
          </small>
          <small v-if="latest.error_message" class="waajo-ticket-followup-error-text">{{ latest.error_message }}</small>
        </div>

        <div v-if="latest.messages && latest.messages.length" class="waajo-ticket-followup-steps">
          <div v-for="message in latest.messages" :key="message.id" class="waajo-ticket-followup-step">
            <span class="waajo-ticket-followup-step-icon" :class="'is-' + message.status">
              <i :class="messageIcon(message.status)"></i>
            </span>
            <div class="waajo-ticket-followup-step-copy">
              <div class="waajo-ticket-followup-step-top">
                <strong>{{ message.step_order }}. {{ message.step_name || 'Tahap' }}</strong>
                <span class="waajo-ticket-followup-mini-badge" :class="'is-' + message.status">{{ messageStatusLabel(message.status) }}</span>
              </div>
              <p>{{ message.message_content || message.rendered_content || '-' }}</p>
              <small>
                Jadwal: {{ formatTicketDateTime(message.scheduled_at) }}
                <template v-if="message.sent_at"> · Terkirim: {{ formatTicketDateTime(message.sent_at) }}</template>
              </small>
              <small v-if="message.error_message" class="waajo-ticket-followup-error-text">{{ message.error_message }}</small>
            </div>
          </div>
        </div>

        <div class="waajo-ticket-followup-actions">
          <button v-if="canCancel" type="button" class="waajo-ticket-btn is-block" :disabled="cancelling" @click="cancelRun">
            <i v-if="cancelling" class="fa fa-spinner fa-spin"></i>
            {{ cancelling ? 'Membatalkan...' : 'Batalkan Auto Follow-up' }}
          </button>
          <button v-if="canSchedule" type="button" class="waajo-ticket-btn is-primary is-block" :disabled="submitting" @click="openPicker">
            <i class="fa fa-calendar-plus-o"></i> Jadwalkan Rangkaian Baru
          </button>
          <small v-if="!canSchedule && blockedReason" class="waajo-ticket-followup-note">{{ blockedReason }}</small>
        </div>

        <div v-if="runs.length > 1" class="waajo-ticket-followup-history">
          <span>Riwayat rangkaian</span>
          <div v-for="run in runs.slice(1)" :key="run.id" class="waajo-ticket-followup-history-item">
            <strong>{{ run.template_name }}</strong>
            <small v-if="run.batch_name">Batch: {{ run.batch_name }}</small>
            <small>{{ formatTicketDateTime(run.create_datetime) }} · {{ statusLabel(run.status) }}</small>
          </div>
        </div>
      </template>
    </template>

    <div v-if="showPicker" class="waajo-ticket-followup-modal-backdrop" @click.self="closePicker">
      <div class="waajo-ticket-followup-modal" role="dialog" aria-modal="true">
        <div class="waajo-ticket-followup-modal-head">
          <div>
            <h5>Jadwalkan Auto Follow-up</h5>
            <p>Pilih template existing. Pesan baru dijadwalkan setelah konfirmasi.</p>
          </div>
          <button type="button" class="waajo-ticket-followup-modal-close" :disabled="submitting" @click="closePicker">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="waajo-ticket-followup-modal-body">
          <div class="waajo-ticket-followup-target">
            <div><span>Pelanggan</span><strong>{{ ticket.contact_name || ticket.contact_number }}</strong></div>
            <div><span>Nomor WhatsApp CS</span><strong>{{ formatTicketPhone(ticket.license_phone_number, ticket.license_name || '-') }}</strong></div>
          </div>
          <p class="waajo-ticket-followup-recipient-note">
            Rangkaian ini hanya dikirim ke pelanggan tiket ini melalui Nomor WhatsApp CS di atas. Sumber kontak template tidak dipakai.
          </p>

          <div v-if="!selectedTemplate" class="waajo-ticket-followup-template-picker">
            <div class="waajo-ticket-followup-search">
              <i class="fa fa-search"></i>
              <input v-model.trim="templateSearch" type="text" placeholder="Cari template Auto Follow-up..." @input="onTemplateSearch" @keyup.enter="loadTemplates">
            </div>
            <div v-if="templatesLoading" class="waajo-ticket-followup-state">
              <i class="fa fa-spinner fa-spin"></i> Memuat template...
            </div>
            <div v-else-if="templatesError" class="waajo-ticket-followup-state is-error">
              <i class="fa fa-exclamation-circle"></i>
              <span>{{ templatesError }}</span>
              <button type="button" @click="loadTemplates">Coba lagi</button>
            </div>
            <div v-else-if="!templates.length" class="waajo-ticket-followup-empty-template">
              <i class="fa fa-folder-open-o"></i>
              <strong>Belum ada template Auto Follow-up.</strong>
              <p>Buat template dari halaman Auto Follow-up existing, lalu kembali ke detail tiket ini.</p>
              <div class="waajo-ticket-followup-empty-actions">
                <button type="button" class="waajo-ticket-btn is-primary" @click="goToAfuList">
                  Buat Template Auto Follow-up
                </button>
                <button type="button" class="waajo-ticket-btn" @click="goToAfuTemplates">
                  Kelola Template
                </button>
              </div>
            </div>
            <div v-else class="waajo-ticket-followup-template-list">
              <button
                v-for="template in templates"
                :key="template.id"
                type="button"
                class="waajo-ticket-followup-template-option"
                @click="selectTemplate(template)"
              >
                <span><strong>{{ template.name }}</strong><small>{{ template.description || 'Tanpa deskripsi' }}</small></span>
                <i class="fa fa-angle-right"></i>
              </button>
            </div>
          </div>

          <div v-else class="waajo-ticket-followup-preview">
            <button type="button" class="waajo-ticket-followup-back" :disabled="submitting" @click="selectedTemplate = null">
              <i class="fa fa-angle-left"></i> Ganti template
            </button>
            <div v-if="detailLoading" class="waajo-ticket-followup-state">
              <i class="fa fa-spinner fa-spin"></i> Memuat detail template...
            </div>
            <div v-else-if="detailError" class="waajo-ticket-followup-state is-error">
              <i class="fa fa-exclamation-circle"></i>
              <span>{{ detailError }}</span>
              <button type="button" @click="selectTemplate(selectedTemplate)">Coba lagi</button>
            </div>
            <template v-else-if="templateDetail">
              <h6>{{ templateDetail.name }}</h6>
              <p class="waajo-ticket-followup-preview-desc">{{ templateDetail.description || 'Tanpa deskripsi' }}</p>
              <div class="waajo-ticket-followup-meta">
                <div><span>Jumlah tahap</span><strong>{{ templateSteps.length }}</strong></div>
                <div><span>Jendela kirim</span><strong>{{ templateConfig.sending_start_time || '-' }}–{{ templateConfig.sending_end_time || '-' }}</strong></div>
                <div><span>Berhenti saat dibalas</span><strong>{{ Number(templateConfig.stop_on_customer_reply) === 1 ? 'Ya' : 'Tidak' }}</strong></div>
                <div><span>Batas harian</span><strong>{{ templateConfig.daily_contact_limit || '-' }} kontak</strong></div>
              </div>
              <div class="waajo-ticket-followup-steps">
                <div v-for="step in templateSteps" :key="step.step_order" class="waajo-ticket-followup-step">
                  <span class="waajo-ticket-followup-step-icon"><i class="fa fa-comment"></i></span>
                  <div class="waajo-ticket-followup-step-copy">
                    <div class="waajo-ticket-followup-step-top">
                      <strong>{{ step.step_order }}. {{ step.name || 'Tahap' }}</strong>
                      <small>{{ step.delay_value }} {{ step.delay_unit }}</small>
                    </div>
                    <p>{{ step.message_content || '-' }}</p>
                  </div>
                </div>
              </div>
              <div class="waajo-ticket-followup-modal-footer">
                <button type="button" class="waajo-ticket-btn" :disabled="submitting" @click="closePicker">Batal</button>
                <button type="button" class="waajo-ticket-btn is-primary" :disabled="submitting || confirming" @click="confirmSchedule">
                  <i v-if="submitting || confirming" class="fa fa-spinner fa-spin"></i>
                  {{ submitting || confirming ? 'Menjadwalkan...' : 'Jadwalkan Auto Follow-up' }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Swal from 'sweetalert2'
import { formatTicketDateTime, formatTicketPhone, extractApiErrorMessage } from '@/utils/ticket'

export default {
  name: 'TicketFollowUpPanel',
  props: {
    ticket: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      runs: [],
      latest: null,
      canSchedule: false,
      blockedReason: '',
      statusRequestId: 0,
      showPicker: false,
      templates: [],
      templateSearch: '',
      templatesLoading: false,
      templatesError: '',
      templatesRequestId: 0,
      templateSearchTimer: null,
      selectedTemplate: null,
      templateDetail: null,
      detailLoading: false,
      detailError: '',
      detailRequestId: 0,
      submitting: false,
      confirming: false,
      scheduleError: '',
      cancelling: false
    }
  },
  computed: {
    templateConfig() {
      return this.templateDetail && this.templateDetail.config ? this.templateDetail.config : {}
    },
    templateSteps() {
      return Array.isArray(this.templateConfig.steps) ? this.templateConfig.steps : []
    },
    canCancel() {
      return !!this.latest && (this.latest.status === 'active' || this.latest.status === 'scheduled' || this.latest.status === 'paused')
    }
  },
  watch: {
    'ticket.id': function() {
      this.runs = []
      this.latest = null
      this.refresh()
    },
    'ticket.status': function() {
      this.refresh()
    }
  },
  created() {
    this.refresh()
  },
  beforeDestroy() {
    if (this.templateSearchTimer) {
      clearTimeout(this.templateSearchTimer)
    }
  },
  methods: {
    formatTicketDateTime,
    formatTicketPhone,
    onTemplateSearch() {
      if (this.templateSearchTimer) clearTimeout(this.templateSearchTimer)
      this.templateSearchTimer = setTimeout(() => {
        this.templateSearchTimer = null
        this.loadTemplates()
      }, 350)
    },
    async refresh() {
      if (!this.ticket || !this.ticket.id) return
      const requestId = this.statusRequestId + 1
      this.statusRequestId = requestId
      this.loading = !this.latest
      this.error = ''
      try {
        const response = await this.$store.dispatch('ticket/followUp', { id: this.ticket.id })
        if (requestId !== this.statusRequestId) return
        const payload = response && response.data ? response.data : {}
        this.runs = Array.isArray(payload.runs) ? payload.runs : []
        this.latest = payload.latest || (this.runs.length ? this.runs[0] : null)
        this.canSchedule = !!payload.can_schedule
        this.blockedReason = payload.schedule_blocked_reason || ''
      } catch (error) {
        if (requestId !== this.statusRequestId) return
        this.error = extractApiErrorMessage(error, 'Status Auto Follow-up belum dapat dimuat.')
      } finally {
        if (requestId === this.statusRequestId) this.loading = false
      }
    },
    openPicker() {
      if (!this.canSchedule || this.submitting) return
      this.showPicker = true
      this.selectedTemplate = null
      this.templateDetail = null
      this.detailError = ''
      if (!this.templates.length) this.loadTemplates()
    },
    closePicker() {
      if (this.submitting || this.confirming) return
      this.showPicker = false
      this.selectedTemplate = null
      this.templateDetail = null
    },
    async loadTemplates() {
      const requestId = this.templatesRequestId + 1
      this.templatesRequestId = requestId
      this.templatesLoading = true
      this.templatesError = ''
      try {
        const response = await this.$store.dispatch('autoFollowUp/templateListAutoFollowUp', {
          page: 1,
          per_page: 20,
          search: this.templateSearch
        })
        if (requestId !== this.templatesRequestId) return
        // requestRaw mengembalikan envelope {status,msg,data}; data berisi
        // {page, perPage, totalRecords, data:[...]}.
        const envelope = response || {}
        const payload = envelope.data !== undefined ? envelope.data : envelope
        const rows = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload.data) ? payload.data : [])
        this.templates = rows
      } catch (error) {
        if (requestId !== this.templatesRequestId) return
        this.templates = []
        this.templatesError = extractApiErrorMessage(error, 'Template Auto Follow-up gagal dimuat.')
      } finally {
        if (requestId === this.templatesRequestId) this.templatesLoading = false
      }
    },
    goToAfuList() {
      this.showPicker = false
      this.$router.push('/auto-follow-up')
    },
    goToAfuTemplates() {
      this.showPicker = false
      this.$router.push('/auto-follow-up/templates')
    },
    async selectTemplate(template) {
      const requestId = this.detailRequestId + 1
      this.detailRequestId = requestId
      this.selectedTemplate = template
      this.detailLoading = true
      this.detailError = ''
      try {
        const response = await this.$store.dispatch('autoFollowUp/templateDetailAutoFollowUp', { id: template.id })
        if (requestId !== this.detailRequestId) return
        this.templateDetail = response && response.data ? response.data : null
      } catch (error) {
        if (requestId !== this.detailRequestId) return
        this.templateDetail = null
        this.detailError = extractApiErrorMessage(error, 'Detail template gagal dimuat.')
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
      }
    },
    swalAboveModal(options) {
      const base = options || {}
      const customClass = Object.assign({}, base.customClass || {}, {
        container: 'waajo-swal-above-ticket-modal'
      })
      return this.$fire(Object.assign({}, base, {
        customClass,
        customContainerClass: 'waajo-swal-above-ticket-modal'
      }))
    },
    validateScheduleData(ticketId, template, templateDetail, contactNumber, licenseId) {
      if (!ticketId || ticketId <= 0) {
        return 'ID tiket belum tersedia. Muat ulang detail tiket.'
      }
      if (!template || !String(template.id || '').trim()) {
        return 'Template belum dipilih dengan benar. Pilih ulang template.'
      }
      if (!templateDetail) {
        return 'Detail template belum berhasil dimuat. Coba muat ulang template.'
      }
      const steps = templateDetail.config && templateDetail.config.steps
      if (!Array.isArray(steps) || !steps.length) {
        return 'Template tidak memiliki tahapan pesan yang dapat dijadwalkan.'
      }
      if (this.ticket.status !== 'resolved') {
        return 'Auto Follow-up hanya dapat dijadwalkan setelah tiket Resolved.'
      }
      if (!contactNumber) {
        return 'Nomor pelanggan tiket belum tersedia. Lengkapi data tiket terlebih dahulu.'
      }
      if (!licenseId) {
        return 'Nomor WhatsApp CS tiket belum tersedia. Lengkapi data tiket terlebih dahulu.'
      }
      return ''
    },
    async performSchedule(ticketId, templateId) {
      if (this.submitting) {
        throw new Error('Penjadwalan sedang diproses.')
      }
      this.submitting = true
      try {
        const response = await this.$store.dispatch('ticket/scheduleFollowUp', {
          ticket_id: ticketId,
          template_id: templateId
        })
        const payload = response && response.data ? response.data : null
        if (!payload || (!payload.id && !payload.auto_follow_up_id)) {
          throw new Error('Backend tidak mengembalikan data penjadwalan yang valid.')
        }
        return payload
      } finally {
        this.submitting = false
      }
    },
    async confirmSchedule() {
      if (this.submitting || this.confirming) return

      const ticketId = Number(this.ticket && this.ticket.id)
      const template = this.selectedTemplate
      const templateId = template ? String(template.id || '').trim() : ''
      const templateName = template && template.name ? String(template.name) : 'Auto Follow-up'
      const contactNumber = String(
        (this.ticket && this.ticket.contact_number) || ''
      ).trim()
      const licenseId = String((this.ticket && this.ticket.license_id) || '').trim()
      const validationError = this.validateScheduleData(
        ticketId,
        template,
        this.templateDetail,
        contactNumber,
        licenseId
      )
      if (validationError) {
        this.swalAboveModal({
          title: 'Data Belum Lengkap',
          text: validationError,
          type: 'warning'
        })
        return
      }

      // Snapshot ID sebelum dialog konfirmasi. Dialog SweetAlert berada di
      // body; jangan biarkan perubahan state modal mengubah objek yang dipakai
      // request.
      this.confirming = true
      this.scheduleError = ''
      try {
        const confirmation = await this.swalAboveModal({
          title: 'Jadwalkan Auto Follow-up?',
          text:
            'Template "' +
            templateName +
            '" akan dijadwalkan untuk ' +
            (this.ticket.contact_name || contactNumber) +
            ' melalui ' +
            this.formatTicketPhone(this.ticket.license_phone_number, this.ticket.license_name || '-') +
            '. Pesan tidak dikirim sebelum jadwal tiba.',
          showCancelButton: true,
          showLoaderOnConfirm: true,
          confirmButtonText: 'Ya, jadwalkan',
          cancelButtonText: 'Batal',
          type: 'warning',
          confirmButtonColor: '#f39c12',
          preConfirm: () => {
            this.scheduleError = ''
            if (typeof Swal.resetValidationMessage === 'function') {
              Swal.resetValidationMessage()
            }
            return this.performSchedule(ticketId, templateId)
              .then(() => true)
              .catch(error => {
                this.scheduleError = extractApiErrorMessage(
                  error,
                  'Auto Follow-up gagal dijadwalkan.'
                )
                // SweetAlert2 v8: preConfirm false menjaga dialog tetap
                // terbuka; tampilkan error pada dialog agar admin dapat
                // mencoba lagi atau membatalkan tanpa kehilangan preview.
                if (typeof Swal.showValidationMessage === 'function') {
                  Swal.showValidationMessage(this.scheduleError)
                }
                return false
              })
          }
        })

        if (!confirmation || confirmation.value !== true) {
          if (this.scheduleError) {
            this.swalAboveModal({
              title: 'Gagal Menjadwalkan',
              text: this.scheduleError,
              type: 'error'
            })
            this.scheduleError = ''
          }
          return
        }

        this.showPicker = false
        this.selectedTemplate = null
        this.templateDetail = null
        await this.refresh()
        if (this.error) {
          this.swalAboveModal({
            title: 'Dijadwalkan, Status Gagal Dimuat',
            text: 'Auto Follow-up berhasil dijadwalkan, tetapi status terbaru belum dapat dimuat. Muat ulang panel.',
            type: 'warning'
          })
        } else {
          this.swalAboveModal({
            title: 'Berhasil',
            text: 'Auto Follow-up berhasil dijadwalkan.',
            type: 'success',
            timer: 2600
          })
        }
        this.$emit('scheduled')
      } catch (error) {
        this.swalAboveModal({
          title: 'Gagal Menjadwalkan',
          text: extractApiErrorMessage(error, 'Auto Follow-up gagal dijadwalkan.'),
          type: 'error'
        })
      } finally {
        this.confirming = false
      }
    },
    async cancelRun() {
      if (this.cancelling) return
      const confirmation = await this.swalAboveModal({
        title: 'Batalkan Auto Follow-up?',
        text: 'Pesan yang belum dikirim akan dibatalkan. Pesan yang sudah diproses worker atau terkirim tidak dapat ditarik.',
        showCancelButton: true,
        confirmButtonText: 'Ya, batalkan',
        cancelButtonText: 'Batal',
        type: 'warning',
        confirmButtonColor: '#f39c12'
      })
      if (!confirmation || confirmation.value !== true) return
      this.cancelling = true
      try {
        await this.$store.dispatch('ticket/cancelFollowUp', {
          ticket_id: this.ticket.id,
          reason: 'manual_cancel'
        })
        await this.refresh()
        this.swalAboveModal({
          title: 'Berhasil',
          text: 'Auto Follow-up dibatalkan.',
          type: 'success',
          timer: 2400
        })
        this.$emit('cancelled')
      } catch (error) {
        this.swalAboveModal({
          title: 'Gagal Membatalkan',
          text: extractApiErrorMessage(error, 'Auto Follow-up gagal dibatalkan.'),
          type: 'error'
        })
      } finally {
        this.cancelling = false
      }
    },
    statusLabel(value) {
      const labels = {
        scheduled: 'Dijadwalkan',
        active: 'Aktif',
        paused: 'Dijeda',
        completed: 'Selesai',
        cancelled: 'Dibatalkan',
        failed: 'Gagal'
      }
      return labels[value] || value || '-'
    },
    recipientLabel(value) {
      const labels = {
        pending: 'Menunggu',
        in_progress: 'Berjalan',
        replied: 'Pelanggan membalas',
        completed: 'Selesai',
        stopped: 'Dihentikan',
        failed: 'Gagal'
      }
      return labels[value] || value || '-'
    },
    messageStatusLabel(value) {
      const labels = {
        scheduled: 'Dijadwalkan',
        queued: 'Dalam antrean',
        processing: 'Diproses',
        sent: 'Terkirim',
        failed: 'Gagal',
        skipped: 'Dilewati',
        cancelled: 'Dibatalkan'
      }
      return labels[value] || value || '-'
    },
    messageIcon(value) {
      if (value === 'sent') return 'fa fa-check'
      if (value === 'failed') return 'fa fa-times'
      if (value === 'skipped' || value === 'cancelled') return 'fa fa-ban'
      if (value === 'processing' || value === 'queued') return 'fa fa-spinner'
      return 'fa fa-clock-o'
    },
    stopReasonLabel(value) {
      if (value === 'customer_replied') return 'Rangkaian berhenti karena pelanggan membalas.'
      if (value === 'ticket_reopened') return 'Rangkaian dibatalkan karena tiket dibuka kembali.'
      if (value === 'manual_cancel') return 'Rangkaian dibatalkan oleh admin.'
      if (value === 'completed') return 'Rangkaian selesai.'
      return value || ''
    }
  }
}
</script>

<style scoped>
.waajo-ticket-followup-panel {
  overflow: visible;
  padding: 18px;
  border: 1px solid var(--waajo-border);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}
.waajo-ticket-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.waajo-ticket-panel-head h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--waajo-text);
}
.waajo-ticket-panel-head h4 i {
  color: var(--waajo-brand);
  margin-right: 7px;
}
.waajo-ticket-followup-loading {
  padding: 8px 0;
}
.waajo-ticket-followup-sk {
  display: block;
  height: 12px;
  width: 100%;
  margin-bottom: 8px;
  border-radius: 5px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: waajoTicketFollowUpSk 1.2s infinite;
}
.waajo-ticket-followup-sk.is-short {
  width: 65%;
}
@keyframes waajoTicketFollowUpSk {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.waajo-ticket-followup-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 0 2px;
  text-align: center;
  color: var(--waajo-text-muted);
  font-size: 13px;
}
.waajo-ticket-followup-empty i {
  font-size: 24px;
  color: #cbd5e1;
}
.waajo-ticket-followup-empty p {
  margin: 0;
  line-height: 1.5;
}
.waajo-ticket-followup-card {
  padding: 12px;
  border: 1px solid var(--waajo-border);
  border-radius: 10px;
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-followup-card.is-cancelled,
.waajo-ticket-followup-card.is-failed {
  background: #fef2f2;
  border-color: #fecaca;
}
.waajo-ticket-followup-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 9px;
}
.waajo-ticket-followup-card-top strong {
  color: var(--waajo-text);
  font-size: 13px;
  line-height: 1.4;
}
.waajo-ticket-followup-batch {
  display: block;
  margin-top: 2px;
  color: var(--waajo-brand-ink);
  font-size: 10.5px;
  line-height: 1.4;
}
.waajo-ticket-followup-badge,
.waajo-ticket-followup-mini-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  background: #f1f5f9;
  color: #475467;
}
.waajo-ticket-followup-badge.is-active,
.waajo-ticket-followup-mini-badge.is-sent,
.waajo-ticket-followup-badge.is-completed {
  background: var(--waajo-status-resolved-bg);
  color: var(--waajo-status-resolved-ink);
}
.waajo-ticket-followup-badge.is-scheduled,
.waajo-ticket-followup-mini-badge.is-scheduled,
.waajo-ticket-followup-mini-badge.is-queued,
.waajo-ticket-followup-mini-badge.is-processing {
  background: var(--waajo-brand-soft);
  color: var(--waajo-brand-ink);
}
.waajo-ticket-followup-badge.is-paused {
  background: var(--waajo-status-progress-bg);
  color: var(--waajo-status-progress-ink);
}
.waajo-ticket-followup-badge.is-cancelled,
.waajo-ticket-followup-badge.is-failed,
.waajo-ticket-followup-mini-badge.is-cancelled,
.waajo-ticket-followup-mini-badge.is-failed {
  background: #fef2f2;
  color: #b91c1c;
}
.waajo-ticket-followup-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
}
.waajo-ticket-followup-meta span {
  display: block;
  font-size: 10px;
  color: var(--waajo-text-faint);
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 2px;
}
.waajo-ticket-followup-meta strong {
  display: block;
  font-size: 12px;
  color: var(--waajo-text);
  line-height: 1.4;
}
.waajo-ticket-followup-note {
  display: block;
  margin-top: 8px;
  color: var(--waajo-brand-ink);
  font-size: 11px;
  line-height: 1.5;
}
.waajo-ticket-followup-error-text {
  display: block;
  margin-top: 5px;
  color: #b42318;
  font-size: 11px;
  line-height: 1.5;
}
.waajo-ticket-followup-steps {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 11px;
}
.waajo-ticket-followup-step {
  display: flex;
  gap: 9px;
  padding-top: 9px;
  border-top: 1px solid #f0f3f8;
}
.waajo-ticket-followup-step-icon {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--waajo-brand-soft);
  color: var(--waajo-brand-ink);
  font-size: 11px;
}
.waajo-ticket-followup-step-icon.is-sent {
  background: var(--waajo-status-resolved-bg);
  color: var(--waajo-status-resolved-ink);
}
.waajo-ticket-followup-step-icon.is-failed,
.waajo-ticket-followup-step-icon.is-cancelled {
  background: #fef2f2;
  color: #b91c1c;
}
.waajo-ticket-followup-step-copy {
  min-width: 0;
  flex: 1;
}
.waajo-ticket-followup-step-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.waajo-ticket-followup-step-top strong {
  font-size: 12px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-step-top small {
  font-size: 10px;
  color: var(--waajo-text-faint);
}
.waajo-ticket-followup-step-copy p {
  margin: 3px 0 3px;
  font-size: 12px;
  color: var(--waajo-text-body);
  line-height: 1.5;
  white-space: pre-wrap;
}
.waajo-ticket-followup-step-copy > small {
  display: block;
  font-size: 10px;
  color: var(--waajo-text-faint);
}
.waajo-ticket-followup-actions {
  margin-top: 10px;
}
.waajo-ticket-followup-history {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid var(--waajo-border);
}
.waajo-ticket-followup-history > span {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--waajo-text-faint);
  margin-bottom: 6px;
}
.waajo-ticket-followup-history-item {
  padding: 6px 0;
  border-bottom: 1px solid #f0f3f8;
}
.waajo-ticket-followup-history-item strong {
  display: block;
  font-size: 11px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-history-item small {
  display: block;
  margin-top: 2px;
  font-size: 10px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-followup-error {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: #b42318;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-ticket-followup-error button {
  border: 0;
  background: transparent;
  color: #b42318;
  font-size: 11px;
  font-weight: 700;
  text-decoration: underline;
  padding: 0;
}
.waajo-ticket-followup-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 34px 16px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.58);
}
.waajo-ticket-followup-modal {
  width: 100%;
  max-width: 720px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  overflow: hidden;
}
.waajo-ticket-followup-modal-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 22px 15px;
  border-bottom: 1px solid var(--waajo-border);
}
.waajo-ticket-followup-modal-head h5 {
  margin: 0 0 4px;
  font-size: 19px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-modal-head p {
  margin: 0;
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-followup-modal-close {
  border: 0;
  background: transparent;
  color: var(--waajo-text-muted);
  font-size: 18px;
}
.waajo-ticket-followup-modal-body {
  padding: 18px 22px 22px;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
}
.waajo-ticket-followup-target {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 11px 13px;
  margin-bottom: 15px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 9px;
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-followup-target span {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--waajo-brand-ink);
  margin-bottom: 2px;
}
.waajo-ticket-followup-target strong {
  display: block;
  font-size: 13px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-recipient-note {
  margin: -5px 0 13px;
  color: var(--waajo-text-muted);
  font-size: 12px;
  line-height: 1.5;
}
.waajo-ticket-followup-search {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 11px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 10px;
}
.waajo-ticket-followup-search:focus-within {
  border-color: var(--waajo-brand-border-strong);
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-ticket-followup-search i {
  color: var(--waajo-text-faint);
}
.waajo-ticket-followup-search input {
  flex: 1;
  border: 0;
  outline: none;
  min-height: 38px;
  font-size: 14px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-template-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
}
.waajo-ticket-followup-template-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 11px;
  border: 1px solid var(--waajo-border);
  border-radius: 8px;
  background: #fff;
  text-align: left;
}
.waajo-ticket-followup-template-option:hover {
  border-color: var(--waajo-brand-border);
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-followup-template-option strong {
  display: block;
  font-size: 13px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-template-option small {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-followup-template-option > i {
  color: var(--waajo-text-faint);
}
.waajo-ticket-followup-state {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 14px 4px;
  color: var(--waajo-text-muted);
  font-size: 13px;
}
.waajo-ticket-followup-state.is-error {
  color: #b42318;
}
.waajo-ticket-followup-state button {
  border: 0;
  background: transparent;
  color: #b42318;
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
}
.waajo-ticket-followup-empty-template {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  padding: 18px 12px;
  text-align: center;
  color: var(--waajo-text-muted);
}
.waajo-ticket-followup-empty-template > i {
  font-size: 28px;
  color: #cbd5e1;
}
.waajo-ticket-followup-empty-template strong {
  color: var(--waajo-text);
  font-size: 14px;
}
.waajo-ticket-followup-empty-template p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-ticket-followup-empty-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}
.waajo-ticket-followup-back {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 12px;
  font-weight: 700;
  padding: 0 0 10px;
}
.waajo-ticket-followup-preview h6 {
  margin: 0 0 4px;
  font-size: 16px;
  color: var(--waajo-text);
}
.waajo-ticket-followup-preview-desc {
  margin: 0 0 12px;
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-followup-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid var(--waajo-border);
}
.waajo-ticket-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 15px;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  background: #fff;
  color: var(--waajo-text-body);
  font-size: 14px;
  font-weight: 600;
}
.waajo-ticket-btn.is-primary {
  border-color: var(--waajo-brand);
  background: var(--waajo-brand);
  color: #fff;
  font-weight: 700;
}
.waajo-ticket-btn.is-block {
  display: flex;
  width: 100%;
  margin-top: 8px;
}
.waajo-ticket-btn:disabled {
  opacity: .6;
}
.waajo-ticket-link-btn {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 13px;
  font-weight: 700;
  padding: 0;
}
@media (max-width: 640px) {
  .waajo-ticket-followup-meta,
  .waajo-ticket-followup-target {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketFollowUpPanel.vue